import { useId, type ReactNode } from "react";
import { IconInfoCircle } from "@tabler/icons-react";
import { IconTooltip } from "@/components/IconTooltip/IconTooltip";

type SectionCardProps = {
  title: string;
  /** Short explanation behind the info icon next to the title. */
  info?: string;
  headerRight?: ReactNode;
  tone?: "default" | "danger";
  children: ReactNode;
  /** Body padding; `p-0` for lists whose rows bring their own. */
  bodyClassName?: string;
  testId?: string;
};

/**
 * A bordered card with a gray header strip (title + optional info icon) and a
 * white body — the section shape of the member and role pages.
 */
export function SectionCard({
  title,
  info,
  headerRight,
  tone = "default",
  children,
  bodyClassName = "p-4",
  testId,
}: SectionCardProps) {
  const headingId = useId();
  const border = tone === "danger" ? "outline-error" : "outline-quaternary";
  // No overflow clipping: a field's suggestion list must reach past the card;
  // the header strip rounds its own top corners instead.
  return (
    <section
      aria-labelledby={headingId}
      className={`rounded-xl border ${border} surface-primary`}
      data-testid={testId}
    >
      <div className={`flex flex-row items-center justify-between gap-3 px-4 py-2.5 rounded-t-xl surface-secondary border-b ${border}`}>
        <div className="flex flex-row items-center gap-1.5 min-w-0">
          <h2
            id={headingId}
            className={`body-2-bold truncate ${tone === "danger" ? "font-error" : "font-secondary"}`}
          >
            {title}
          </h2>
          {info && <IconTooltip icon={IconInfoCircle} tooltip={info} />}
        </div>
        {headerRight}
      </div>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}
